/**
 * sound.js — Procedural sound effects for Sky-Drop.
 *
 * All effects are synthesised at runtime with the Web Audio API, so the game
 * ships with zero audio assets and zero external dependencies. A single shared
 * AudioContext is created lazily on the first user gesture (browsers block
 * audio that starts before one), driven by oscillators and short noise bursts
 * shaped with gain envelopes.
 *
 * Public API:
 *   initAudio()        — unlock/resume the context; call from a click handler.
 *   playSound(name)    — play one of the named effects (see SOUNDS below).
 *   setMuted(bool) / isMuted() — global mute toggle.
 */

// Shared audio graph. Created on first use so we never touch Web Audio before
// a user gesture has occurred (which would leave the context suspended).
let audioCtx = null;
let masterGain = null;
let muted = false;

// Master output level for all effects (0..1). Kept modest so overlapping
// effects during frantic combat don't clip.
const MASTER_VOLUME = 0.25;

/**
 * Lazily create (or resume) the shared AudioContext and master gain node.
 * Safe to call repeatedly. Returns the context, or null if Web Audio is
 * unavailable in the current environment.
 */
function ensureContext() {
  if (typeof window === 'undefined') return null;

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;

  // First-time construction of the audio graph: source nodes -> masterGain ->
  // speakers. Individual effects create their own oscillator/gain nodes and
  // connect into masterGain.
  if (!audioCtx) {
    audioCtx = new AudioCtx();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = MASTER_VOLUME;
    masterGain.connect(audioCtx.destination);
  }

  // Autoplay policies leave the context "suspended" until a gesture resumes it.
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }

  return audioCtx;
}

/**
 * Play a single tone with a linear attack and exponential decay.
 *
 * @param {object} opts
 * @param {number} opts.freq      Starting frequency in Hz.
 * @param {number} [opts.slideTo] Optional target frequency to glide toward.
 * @param {string} [opts.type]    Oscillator waveform ('sine'|'square'|'sawtooth'|'triangle').
 * @param {number} [opts.duration] Length of the effect in seconds.
 * @param {number} [opts.gain]    Peak gain for this tone (0..1).
 * @param {number} [opts.delay]   Seconds to wait before the tone starts.
 */
function tone({ freq, slideTo, type = 'square', duration = 0.15, gain = 0.4, delay = 0 }) {
  const ctx = ensureContext();
  if (!ctx) return;

  const start = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (slideTo) {
    // Exponential ramps can't reach 0, so slideTo is always a positive Hz value.
    osc.frequency.exponentialRampToValueAtTime(slideTo, start + duration);
  }

  // Fast attack, exponential decay to near-silence — a punchy, retro envelope.
  env.gain.setValueAtTime(0.0001, start);
  env.gain.linearRampToValueAtTime(gain, start + 0.01);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  osc.connect(env);
  env.connect(masterGain);

  osc.start(start);
  osc.stop(start + duration + 0.02);
}

/**
 * Play a burst of filtered white noise — the basis for explosions/vaporization.
 *
 * @param {object} opts
 * @param {number} [opts.duration] Length of the burst in seconds.
 * @param {number} [opts.gain]     Peak gain (0..1).
 * @param {number} [opts.cutoff]   Low-pass cutoff frequency in Hz.
 */
function noiseBurst({ duration = 0.3, gain = 0.4, cutoff = 1200 }) {
  const ctx = ensureContext();
  if (!ctx) return;

  const start = ctx.currentTime;

  // Fill a short buffer with white noise, played once through a low-pass filter.
  const frameCount = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, frameCount, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frameCount; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const src = ctx.createBufferSource();
  src.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = cutoff;

  const env = ctx.createGain();
  env.gain.setValueAtTime(gain, start);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  src.connect(filter);
  filter.connect(env);
  env.connect(masterGain);

  src.start(start);
  src.stop(start + duration);
}

/**
 * Effect registry. Each entry is a function that builds one sound from the
 * `tone`/`noiseBurst` primitives above. Referenced by name via playSound().
 */
const SOUNDS = {
  // Kick/throw the board — a fast downward whoosh.
  kick: () => tone({ freq: 420, slideTo: 90, type: 'square', duration: 0.18, gain: 0.5 }),

  // Beam windup — rising hum that telegraphs the shot to both players. Its
  // length is tuned to BEAM_CHARGE_TIME so the sound ends as the beam fires.
  beamCharge: () => {
    tone({ freq: 180, slideTo: 900, type: 'sawtooth', duration: 0.28, gain: 0.16 });
    tone({ freq: 360, slideTo: 1800, type: 'sine', duration: 0.28, gain: 0.1 });
  },

  // Full-screen beam — bright sawtooth zap sweeping downward.
  beam: () => {
    tone({ freq: 1400, slideTo: 300, type: 'sawtooth', duration: 0.28, gain: 0.4 });
    tone({ freq: 700, slideTo: 200, type: 'square', duration: 0.28, gain: 0.2 });
  },

  // Board connects and knocks a rider off — blunt thud plus a scrape.
  dismount: () => {
    noiseBurst({ duration: 0.16, gain: 0.35, cutoff: 700 });
    tone({ freq: 260, slideTo: 120, type: 'square', duration: 0.14, gain: 0.3 });
  },

  // A melee strike connecting — short, heavy body impact.
  hit: () => {
    noiseBurst({ duration: 0.2, gain: 0.45, cutoff: 500 });
    tone({ freq: 150, slideTo: 60, type: 'triangle', duration: 0.22, gain: 0.4 });
  },

  // Throwing a punch. Deliberately quiet and dry: it plays on every swing,
  // including the ones that miss, so it has to sit under the impact rather
  // than compete with it.
  punch: () => noiseBurst({ duration: 0.06, gain: 0.16, cutoff: 2200 }),

  // A strike stopped by a guard — bright, hard and short, so it is instantly
  // distinguishable from the dull thud of one that got through. Telling those
  // two apart by ear is most of how a player learns the guard works.
  block: () => {
    noiseBurst({ duration: 0.07, gain: 0.3, cutoff: 5000 });
    tone({ freq: 900, slideTo: 1500, type: 'square', duration: 0.09, gain: 0.22 });
  },

  // Throwing a kick — a longer, lower whoosh than the punch, so the two moves
  // are distinguishable with your eyes on the other fighter.
  swing: () => noiseBurst({ duration: 0.11, gain: 0.2, cutoff: 1100 }),

  // Grabbing an opponent's board — buzzing electric shock.
  shock: () => {
    tone({ freq: 90, type: 'square', duration: 0.3, gain: 0.3 });
    tone({ freq: 1500, slideTo: 400, type: 'sawtooth', duration: 0.3, gain: 0.15 });
  },

  // Player vaporised — noise explosion layered with a descending boom.
  vaporize: () => {
    noiseBurst({ duration: 0.45, gain: 0.5, cutoff: 1600 });
    tone({ freq: 200, slideTo: 40, type: 'triangle', duration: 0.5, gain: 0.4 });
  },

  // Remounting the board — quick rising blip.
  mount: () => tone({ freq: 300, slideTo: 620, type: 'sine', duration: 0.14, gain: 0.35 }),

  // Taking a canister — a bright two-note confirmation, deliberately close to
  // 'mount' in shape so both read as "you now have something".
  pickup: () => {
    tone({ freq: 660, type: 'square', duration: 0.07, gain: 0.28 });
    tone({ freq: 990, type: 'square', duration: 0.1, gain: 0.3, delay: 0.06 });
  },

  // Lobbing a device — a short airy launch, no bass, so it is clearly not the
  // board being kicked.
  lob: () => {
    noiseBurst({ duration: 0.14, gain: 0.16, cutoff: 3000 });
    tone({ freq: 300, slideTo: 700, type: 'sine', duration: 0.16, gain: 0.2 });
  },

  // Cluster submunition — small, dry and repeated, so five of them read as a
  // string of hits rather than one big one.
  clusterBurst: () => {
    noiseBurst({ duration: 0.16, gain: 0.34, cutoff: 2400 });
    tone({ freq: 260, slideTo: 70, type: 'triangle', duration: 0.18, gain: 0.26 });
  },

  // The EMP going off — a swept whine collapsing into a low thump. Tonal where
  // the cluster is noise, because what it does is nothing like an explosion.
  empBurst: () => {
    tone({ freq: 2400, slideTo: 120, type: 'sawtooth', duration: 0.5, gain: 0.3 });
    tone({ freq: 60, type: 'sine', duration: 0.45, gain: 0.35 });
    noiseBurst({ duration: 0.3, gain: 0.18, cutoff: 700 });
  },

  // A board dying under the pulse — the sound of it giving up, played on the
  // rider rather than on the blast.
  boardDown: () => {
    tone({ freq: 520, slideTo: 40, type: 'square', duration: 0.5, gain: 0.28 });
  },

  // Recalling the board — soft two-note beckon.
  recall: () => {
    tone({ freq: 500, type: 'triangle', duration: 0.1, gain: 0.3 });
    tone({ freq: 760, type: 'triangle', duration: 0.12, gain: 0.3, delay: 0.09 });
  },

  // Toggling feet/hands board mode — short mechanical click.
  switchMode: () => tone({ freq: 880, slideTo: 1200, type: 'square', duration: 0.08, gain: 0.3 }),

  // Breaking through into the next screen — a portal whoosh (noise sweep plus
  // a low swell) under the original rising three-note flourish.
  advance: () => {
    noiseBurst({ duration: 0.5, gain: 0.3, cutoff: 900 });
    tone({ freq: 70, slideTo: 320, type: 'sawtooth', duration: 0.45, gain: 0.25 });
    tone({ freq: 440, type: 'square', duration: 0.1, gain: 0.35 });
    tone({ freq: 620, type: 'square', duration: 0.1, gain: 0.35, delay: 0.09 });
    tone({ freq: 880, type: 'square', duration: 0.14, gain: 0.35, delay: 0.18 });
  },

  // Match won — triumphant major arpeggio (C–E–G–C).
  win: () => {
    tone({ freq: 523, type: 'square', duration: 0.16, gain: 0.4 });
    tone({ freq: 659, type: 'square', duration: 0.16, gain: 0.4, delay: 0.15 });
    tone({ freq: 784, type: 'square', duration: 0.16, gain: 0.4, delay: 0.3 });
    tone({ freq: 1046, type: 'square', duration: 0.4, gain: 0.4, delay: 0.45 });
  },
};

/**
 * Unlock/resume the audio context. Call this from a real user gesture (e.g. the
 * "Start Game" button) so later programmatic playSound() calls are audible.
 */
export function initAudio() {
  ensureContext();
}

/**
 * The shared audio graph, for other modules that need to make sound.
 *
 * Exposed so the music player can hang off the SAME context and master gain as
 * the effects rather than building a second one. Two contexts would be two
 * independent volume controls, two separate unlock states, and — on some
 * browsers — a hard limit hit for no reason.
 *
 * @returns {{ctx: AudioContext, master: GainNode}|null} null if Web Audio is
 *   unavailable in this environment.
 */
export function getAudioGraph() {
  const ctx = ensureContext();
  return ctx ? { ctx, master: masterGain } : null;
}

/**
 * Play a named sound effect. No-op if muted, unknown, or Web Audio is
 * unavailable. Never throws — audio must never break the game loop.
 * @param {string} name One of the keys in SOUNDS.
 */
export function playSound(name) {
  if (muted) return;
  const effect = SOUNDS[name];
  if (!effect) return;
  try {
    effect();
  } catch (err) {
    // Swallow audio errors: a dropped sound should never interrupt gameplay.
    console.warn('Sound playback failed:', name, err);
  }
}

/**
 * Enable or disable all sound output.
 *
 * Effects check `muted` when they are triggered, but music is a single node
 * that is already playing, so silencing it means turning the master down. The
 * ramp is short rather than instant because cutting a gain node to zero on a
 * sustained note produces an audible click.
 */
export function setMuted(value) {
  muted = Boolean(value);
  if (!masterGain || !audioCtx) return;
  const now = audioCtx.currentTime;
  masterGain.gain.cancelScheduledValues(now);
  masterGain.gain.setValueAtTime(masterGain.gain.value, now);
  masterGain.gain.linearRampToValueAtTime(muted ? 0 : MASTER_VOLUME, now + 0.08);
}

/** @returns {boolean} whether sound is currently muted. */
export function isMuted() {
  return muted;
}
